import { Button } from '@mui/material';
import { Box } from '@mui/system';
import PropTypes from 'prop-types';
import { Component } from 'react';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    const message = error?.message ? String(error.message) : String(error);

    // MATCH **ONLY** React Minified Errors
    const isMinified = [
      'Minified React error',
      'Failed to load module script',
      'Expected a JavaScript-or-Wasm module script',
      'MIME type of "text/html"',
    ].some((term) => message.includes(term));
    return { hasError: true, error, isMinified };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    const message = error?.message ? String(error.message) : String(error);
    const isMinified = [
      'Minified React error',
      'Failed to load module script',
      'Expected a JavaScript-or-Wasm module script',
      'MIME type of "text/html"',
    ].some((term) => message.includes(term));
    this.setState({ hasError: true, error, isMinified });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  renderErrorUI() {
    const { error } = this.state;
    const status = error?.status;

    const { FallbackComponent } = this.props;
    if (FallbackComponent && this.state.hasError) {
      return <FallbackComponent error={error} onRetry={this.handleRetry} />;
    }
    if (this.state.isMinified) {
      return (
        <Box
          sx={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <h2>You were away from the site for a while.</h2>
          <p>Please refresh the page to load the latest version.</p>
          <Button onClick={() => window.location.reload()}>Refresh Page</Button>
        </Box>
      );
    }

    if (status === 403) {
      return (
        <div style={{ padding: '20px', fontSize: '14px' }}>
          {error?.detail || 'You do not have permission to access this resource.'} Please contact
          support at <a href="mailto:service@qualz.ai">service@qualz.ai</a>
        </div>
      );
    }

    return (
      <div style={{ padding: '20px', fontSize: '14px' }}>
        Something went wrong. Please contact support at{' '}
        <a href="mailto:service@qualz.ai">service@qualz.ai</a>
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      const { fallback: FallbackComponent } = this.props;
      if (typeof FallbackComponent === 'function') {
        return <FallbackComponent error={this.state.error} onRetry={this.handleRetry} />;
      }

      return this.renderErrorUI();
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  fallback: PropTypes.func,
  children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
